import React from 'react';
import { Center, Text, Button, Stack, Paper } from '@mantine/core';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('=== ERROR BOUNDARY CAUGHT ERROR ===');
    console.error('Error:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    console.error('==================================');
  }

  render() {
    if (this.state.hasError) {
      return (
        <Center style={{ height: '100vh' }}>
          <Paper p="xl" radius="md" withBorder>
            <Stack gap="md" align="center">
              <Text size="xl" fw={700} c="red">
                Something went wrong!
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {this.state.error?.message || 'An unknown error occurred'}
              </Text>
              <Text size="xs" c="dimmed" ta="center" style={{ fontFamily: 'monospace', maxWidth: '600px' }}>
                {this.state.error?.stack}
              </Text>
              <Button 
                onClick={() => this.setState({ hasError: false, error: null })}
                variant="light"
              >
                Try again
              </Button>
            </Stack>
          </Paper>
        </Center>
      );
    }

    return this.props.children;
  }
}

